import * as React from 'react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormInput } from '@enginuity-apps-ui-components/molecules/form-elements/form-input/form-input';
import { IAccountManagmentFormOptions, ISubmitStatus } from '../../pages/account/manage-account';
import EditableFormSection from '../../../../../ui-components/library/apps/organisms/editable-section/editable-form-section';
import { FormSelect } from '@enginuity-apps-ui-components/molecules/form-elements/form-select/form-select';
import { OptionTypeBase } from 'react-select';
import { IUserProfileFormData } from '@enginuity-common/types/user';
import { FormRadio } from '@enginuity-apps-ui-components/molecules/form-elements/form-radio/form-radio';

export type IManageAccountEmploymentDetailsProps = {
  onSubmit: (data: IUserProfileFormData) => Promise<ISubmitStatus>;
  data?: IUserProfileFormData;
  intialEditMode?: boolean;
  options: IAccountManagmentFormOptions | null;
};

export default function ManageAccountEmploymentDetails({ onSubmit, data, intialEditMode, options }: IManageAccountEmploymentDetailsProps) {
  const { register, handleSubmit, errors, setValue, watch, triggerValidation } = useForm();
  const [editMode, setEditMode] = useState<boolean>(!!intialEditMode)
  const [submitError, setSubmitError] = useState<JSX.Element | undefined>();
  const [formData, setFormData] = useState<IUserProfileFormData | undefined>(data);

  const saveForm = (submitData: IUserProfileFormData) => {
    if (submitData.employmentStatus === "Unemployed") {
      submitData.employer = "";
    }

    onSubmit(submitData)
      .then((submitStatus: ISubmitStatus) => {
        if (submitStatus.result) {
          setEditMode(false);
        }
        else {
          setSubmitError(submitStatus.error);
        }
      })
  }

  const resetData = (mode: boolean) => {
    setFormData(data && { ...data }); //revert to original data
    setEditMode(mode);
  }

  const [sectorSelected, setSectorSelected] = useState<OptionTypeBase>();

  const handleSectorChange = (selectedOption?: OptionTypeBase) => {
    setValue('sector', selectedOption ? selectedOption.value : "");
    setSectorSelected(selectedOption);
  };

  const handleEmployedChange = (event: React.FormEvent<HTMLInputElement>) => {
    setValue('employmentStatus', event.currentTarget.value);
    triggerValidation('employmentStatus');
  };


  useEffect(() => {
    register({ name: 'sector' });
    register({ name: 'employmentStatus' });
  }, []);

  useEffect(() => {
    setFormData(data);
  }, [data]);

  useEffect(() => {
    if (formData) {
      handleSectorChange(!!formData.sector ? { value: formData.sector, label: formData.sector } : undefined);
    };
    setValue('employmentStatus', formData?.employmentStatus);
  }, [formData]);

  const employmentStatus = watch("employmentStatus");

  return (
    <EditableFormSection
      sectionTitle="04. Employment details"
      onSubmit={handleSubmit(saveForm)}
      submitError={submitError}
      errors={errors}
      editMode={editMode}
      onEditModeChange={(mode: boolean) => resetData(mode)}
    >
      <div className="row spacer-32">
        <div className="col-lg-12">
          <FormRadio
            label={<>{editMode ? "Your employment status *" : "Your employment status"}</>}
            opts={options?.employmentStatus}
            name="employmentStatus"
            onChange={handleEmployedChange}
            errors={errors}
            selectedValue={formData?.employmentStatus}
            showReadOnly={!editMode}
          />
        </div>
      </div>
      {formData && employmentStatus === "Employed" && (
        <div className="row md-spacer-32 spacer-16">
          <div className="col-lg-6">
            <FormInput
              label={<>{editMode ? "Please state your employers name *" : "Your employers name"}</>}
              id="employer"
              defaultValue={formData.employer}
              ref={register({
                required: "Please enter your employer name.", maxLength: {
                  value: 512, message: 'Employer name cannot be more than 50 characters'
                }
              })}
              errors={errors}
              showReadOnly={!editMode}
            />
          </div>
        </div>
      )}
      <div className="row md-spacer-32 spacer-16">
        <div className="col-lg-6 lg-no-spacer spacer-16">
          {options && options.sector && (
            <FormSelect
              label={<>Sector</>}
              id="sector"
              value={sectorSelected}
              options={options.sector}
              handleChange={handleSectorChange}
              errors={errors}
              isSearchable={false}
              showReadOnly={!editMode}
            />
          )}
        </div>
      </div>
      <div className="row spacer-16">
        <div className="col-lg-12">
          <p className="required-field-text color-purple-grey">{editMode ? <>* indicates required field</> : <>&nbsp;</>}</p>
        </div>
      </div>
    </EditableFormSection>
  );
}
